// DerivadorSelector · «Mi médico» en la ficha (Ann, OK 03-11-2026).
//
// UNA sola lista de médicos (la de Config. Clínica → Derivadores), leída de
// GET /api/derivaciones/medicos. Desplegable con búsqueda, «+ Añadir médico»
// inline (POST /api/derivaciones/medicos-rapido) y, si la ficha ya existe,
// cada elección queda registrada como DERIVACIÓN con fecha
// (POST /api/derivaciones) para poder contar por mes y por año.
//
// Para una ficha NUEVA (sin id) no se puede registrar todavía: el padre recibe
// `onDerivacionPendiente(medico, fecha)` y la registra tras crear la ficha.
//
// Sustituye al desplegable con médicos INVENTADOS escritos en código
// («Dr. Martínez…») que había en ClientNewProfile y EmpleadoFicha.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../api";

/** Valor canónico de «¿cómo nos conoció?» = médico. El <select> de la ficha
 *  guardaba «Mi medico» (sin tilde) y el bloque comparaba con «Mi médico»
 *  (con tilde): el selector no aparecía NUNCA. */
export const COMO_NOS_CONOCIO_MEDICO = "Mi medico";
export const esComoNosConocioMedico = (v) =>
  String(v || "").trim().toLowerCase().replace("é", "e") === "mi medico";

const hoyISO = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

export default function DerivadorSelector({
  clienteId,             // id de la ficha (null si es nueva)
  derivadorId,           // client.derivador_id
  derivadorNombre,       // client.derivador_nombre
  isAdmin = false,
  registrar = true,      // false: solo elige médico, no registra derivaciones (EmpleadoFicha)
  onChange,              // ({ derivador_id, derivador_nombre, derivador_especialidad, derivador_centro })
  onDerivacionPendiente, // (medico, fecha) · ficha nueva
}) {
  const [medicos, setMedicos] = useState([]);
  const [catalogos, setCatalogos] = useState({ especialidades: [], centros: [] });
  const [busqueda, setBusqueda] = useState("");
  const [fecha, setFecha] = useState(hoyISO());
  const [derivaciones, setDerivaciones] = useState([]);
  const [mostrarAlta, setMostrarAlta] = useState(false);
  const [nuevo, setNuevo] = useState({ nombre: "", apellidos: "", especialidad_id: "", centro_id: "" });
  const [guardando, setGuardando] = useState(false);

  const cargarMedicos = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/derivaciones/medicos`);
      setMedicos(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      console.error("DerivadorSelector · médicos:", e);
    }
  }, []);

  const cargarDerivaciones = useCallback(async () => {
    if (!clienteId || !registrar) return;
    try {
      const r = await axios.get(`${API}/derivaciones`, { params: { cliente_id: clienteId } });
      setDerivaciones(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      console.error("DerivadorSelector · derivaciones:", e);
    }
  }, [clienteId, registrar]);

  useEffect(() => { cargarMedicos(); cargarDerivaciones(); }, [cargarMedicos, cargarDerivaciones]);

  const filtrados = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    if (!q) return medicos;
    return medicos.filter((m) =>
      `${m.nombre_completo} ${m.especialidad_nombre} ${m.centro_nombre}`.toLowerCase().includes(q));
  }, [medicos, busqueda]);

  const aplicar = async (medico) => {
    onChange && onChange({
      derivador_id: medico.id,
      derivador_nombre: medico.nombre_completo,
      derivador_especialidad: medico.especialidad_nombre || "",
      derivador_centro: medico.centro_nombre || "",
    });
    if (!registrar) return;
    if (!clienteId) {
      onDerivacionPendiente && onDerivacionPendiente(medico, fecha);
      toast.info("El médico se registrará al crear la ficha.");
      return;
    }
    try {
      const r = await axios.post(`${API}/derivaciones`, { cliente_id: clienteId, medico_id: medico.id, fecha });
      toast.success(r.data?.duplicada ? "Esa derivación ya estaba registrada" : `Derivación registrada · ${medico.nombre_completo}`);
      cargarDerivaciones();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(`No se pudo registrar la derivación: ${typeof d === "string" ? d : (e.message || "error")}`);
    }
  };

  const elegir = (id) => {
    if (!id) { onChange && onChange({ derivador_id: null, derivador_nombre: null, derivador_especialidad: null, derivador_centro: null }); return; }
    const m = medicos.find((x) => x.id === id);
    if (m) aplicar(m);
  };

  const abrirAlta = async () => {
    setMostrarAlta(true);
    if (!catalogos.especialidades.length && !catalogos.centros.length) {
      try { const r = await axios.get(`${API}/derivaciones/catalogos`); setCatalogos(r.data || { especialidades: [], centros: [] }); }
      catch (e) { console.error("DerivadorSelector · catálogos:", e); }
    }
  };

  const crearMedico = async () => {
    if (!nuevo.nombre.trim() || !nuevo.apellidos.trim()) { toast.error("Nombre y apellidos del médico son obligatorios"); return; }
    setGuardando(true);
    try {
      const r = await axios.post(`${API}/derivaciones/medicos-rapido`, {
        nombre: nuevo.nombre.trim(), apellidos: nuevo.apellidos.trim(),
        especialidad_id: nuevo.especialidad_id || null, centro_id: nuevo.centro_id || null,
      });
      toast.success(r.data?.existia ? "Ese médico ya existía: se usa el existente" : "Médico añadido a la lista");
      await cargarMedicos();
      setMostrarAlta(false);
      setNuevo({ nombre: "", apellidos: "", especialidad_id: "", centro_id: "" });
      if (r.data?.medico) aplicar(r.data.medico);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(`No se pudo añadir el médico: ${typeof d === "string" ? d : (e.message || "error")}`);
    } finally { setGuardando(false); }
  };

  const anular = async (d) => {
    if (!window.confirm(`¿Anular la derivación de ${d.medico_nombre} (${d.fecha})?`)) return;
    try {
      await axios.delete(`${API}/derivaciones/${d.id}`);
      toast.success("Derivación anulada");
      cargarDerivaciones();
      const viva = derivaciones.find((x) => x.id !== d.id && !x.anulada);
      onChange && onChange(viva
        ? { derivador_id: viva.medico_id, derivador_nombre: viva.medico_nombre, derivador_especialidad: viva.especialidad_nombre, derivador_centro: viva.centro_nombre }
        : { derivador_id: null, derivador_nombre: null, derivador_especialidad: null, derivador_centro: null });
    } catch (e) {
      const dd = e?.response?.data?.detail;
      toast.error(`No se pudo anular: ${typeof dd === "string" ? dd : (e.message || "error")}`);
    }
  };

  const seleccionado = derivadorId || (medicos.find((m) => m.nombre_completo === derivadorNombre)?.id) || "";

  return (
    <div className="cn-data-item" data-testid="derivador-selector"
      style={{ marginTop: "-4px", paddingLeft: "12px", borderLeft: "2px solid var(--cn-primary)" }}>
      <span className="cn-data-label">Médico que deriva</span>
      <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
        <input className="cn-input-editable" placeholder="Buscar médico…" value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)} style={{ flex: 1 }} data-testid="derivador-busqueda" />
        {registrar && (
          <input type="date" className="cn-input-editable" value={fecha} onChange={(e) => setFecha(e.target.value)}
            title="Fecha de la derivación" style={{ width: "140px" }} data-testid="derivador-fecha" />
        )}
      </div>
      <div style={{ display: "flex", gap: "6px", alignItems: "center", marginTop: "4px" }}>
        <select className="cn-input-editable" value={seleccionado} onChange={(e) => elegir(e.target.value)}
          style={{ flex: 1 }} data-testid="derivador-select">
          <option value="">Seleccione un médico…</option>
          {filtrados.map((m) => (
            <option key={m.id} value={m.id}>
              {m.nombre_completo}{m.especialidad_nombre ? ` (${m.especialidad_nombre})` : ""}{m.centro_nombre ? ` · ${m.centro_nombre}` : ""}
            </option>
          ))}
          {seleccionado && !filtrados.some((m) => m.id === seleccionado) && (
            <option value={seleccionado}>{derivadorNombre || "(médico actual)"}</option>
          )}
        </select>
        <button type="button" className="cn-btn cn-btn--outline cn-btn--sm" onClick={abrirAlta} data-testid="derivador-anadir"
          style={{ whiteSpace: "nowrap" }}>+ Añadir médico</button>
      </div>
      {!derivadorId && derivadorNombre && !seleccionado && (
        <div className="text-xs" style={{ color: "#92400e", marginTop: "4px" }}>
          Médico guardado como texto: «{derivadorNombre}». No está en la lista: elígelo o añádelo.
        </div>
      )}

      {mostrarAlta && (
        <div data-testid="derivador-alta" style={{ marginTop: "8px", padding: "8px", border: "1px dashed var(--cn-primary)", borderRadius: "6px" }}>
          <div style={{ display: "flex", gap: "6px" }}>
            <input className="cn-input-editable" placeholder="Nombre" value={nuevo.nombre}
              onChange={(e) => setNuevo({ ...nuevo, nombre: e.target.value })} />
            <input className="cn-input-editable" placeholder="Apellidos" value={nuevo.apellidos}
              onChange={(e) => setNuevo({ ...nuevo, apellidos: e.target.value })} />
          </div>
          <div style={{ display: "flex", gap: "6px", marginTop: "4px" }}>
            <select className="cn-input-editable" value={nuevo.especialidad_id} onChange={(e) => setNuevo({ ...nuevo, especialidad_id: e.target.value })}>
              <option value="">Especialidad (opcional)</option>
              {catalogos.especialidades.map((x) => <option key={x.id} value={x.id}>{x.nombre}</option>)}
            </select>
            <select className="cn-input-editable" value={nuevo.centro_id} onChange={(e) => setNuevo({ ...nuevo, centro_id: e.target.value })}>
              <option value="">Centro (opcional)</option>
              {catalogos.centros.map((x) => <option key={x.id} value={x.id}>{x.nombre}</option>)}
            </select>
          </div>
          <div style={{ display: "flex", gap: "6px", marginTop: "6px", justifyContent: "flex-end" }}>
            <button type="button" className="cn-btn cn-btn--outline cn-btn--sm" onClick={() => setMostrarAlta(false)}>Cancelar</button>
            <button type="button" className="cn-btn cn-btn--primary cn-btn--sm" onClick={crearMedico} disabled={guardando}>
              {guardando ? "Guardando…" : "Guardar médico"}
            </button>
          </div>
        </div>
      )}

      {registrar && clienteId && derivaciones.length > 0 && (
        <div style={{ marginTop: "8px" }} data-testid="derivaciones-lista">
          <span className="cn-data-label">Derivaciones</span>
          <ul style={{ margin: 0, paddingLeft: "0", listStyle: "none" }}>
            {derivaciones.map((d) => (
              <li key={d.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: "12px",
                padding: "2px 0", textDecoration: d.anulada ? "line-through" : "none", color: d.anulada ? "#94a3b8" : "inherit" }}>
                <span>{d.fecha} · {d.medico_nombre}{d.especialidad_nombre ? ` (${d.especialidad_nombre})` : ""}{d.origen === "migracion" ? " · migrada" : ""}</span>
                {isAdmin && !d.anulada && (
                  <button type="button" className="cn-btn cn-btn--outline cn-btn--sm" onClick={() => anular(d)} title="Anular derivación"
                    style={{ color: "#b91c1c", fontSize: "11px" }}>Anular</button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
